import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Field, Empty, NeedClinic, StatusBadge } from "@/components/common";
import { PersonForm, emptyPerson } from "@/components/PersonForm";
import { useAuth } from "@/context/AuthContext";
import { LogoUploader } from "@/components/ClinicLogo";
import { api, errMsg, money, useLoad } from "@/lib/api";
import { can, ROLE_LABELS, STAFF_ROLES } from "@/lib/constants";

const blank = { ...emptyPerson, role: "doctor", department: "", salary: "" };

export default function Staff() {
  const { user, activeClinic, activeClinicId, reloadClinics } = useAuth();
  const [rows, reload] = useLoad(activeClinicId ? "/staff" : null, { clinic_id: activeClinicId });
  const [f, setF] = useState(null);
  const manage = can(user, "manageStaff");
  if (!activeClinic) return <NeedClinic isCeo={user.role === "ceo"} />;
  const save = async () => {
    try {
      await api.post("/staff", { ...f, salary: Number(f.salary || 0) });
      toast.success(`${ROLE_LABELS[f.role]} added`);
      setF(null);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (id, active) => {
    try { await api.patch(`/staff/${id}`, { active }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <PageHeader eyebrow={activeClinic.name} title="Staff & HR" subtitle={manage ? "Appoint staff into departments. They sign in with the email and temporary password you set." : "Clinic staff directory."}>
        {manage && <Button className="rounded-full" onClick={() => setF(blank)} data-testid="add-staff-button"><Plus className="mr-2 h-4 w-4" />Add staff</Button>}
      </PageHeader>
      {manage && (
        <Panel title="Clinic logo" testId="manager-logo-panel">
          <p className="mb-3 text-sm text-slate-500">Shown on invoices and PDF reports. PNG, JPG or WEBP up to 2 MB.</p>
          <LogoUploader clinic={activeClinic} onDone={reloadClinics} />
        </Panel>
      )}
      <Panel testId="staff-table">
        {!rows?.length ? <Empty text="No staff yet." /> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Department</TableHead><TableHead>Contact</TableHead><TableHead>Salary</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>{rows.map((s) => (
              <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                <TableCell className="font-semibold">{s.name}</TableCell>
                <TableCell><span className="rounded-full bg-teal-50 px-2.5 py-0.5 text-xs font-semibold text-teal-700">{ROLE_LABELS[s.role]}</span></TableCell>
                <TableCell>{s.department || "—"}</TableCell>
                <TableCell><p className="text-sm">{s.email}</p><p className="text-xs text-slate-500">{s.phone || ""}</p></TableCell>
                <TableCell>{s.salary ? money(s.salary, activeClinic.currency) : "—"}</TableCell>
                <TableCell>{manage && s.role !== "manager"
                  ? <Switch checked={s.active} onCheckedChange={(v) => toggle(s.id, v)} data-testid={`staff-active-toggle-${s.id}`} />
                  : <StatusBadge status={s.active ? "active" : "inactive"} />}</TableCell>
              </TableRow>))}</TableBody>
          </Table></div>)}
      </Panel>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Add staff member</DialogTitle></DialogHeader>
          {f && <>
            <PersonForm value={f} onChange={setF} prefix="staff" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Role"><Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STAFF_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Department"><Input value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} data-testid="staff-department-input" /></Field>
              <Field label={`Monthly salary (${activeClinic.currency})`}><Input type="number" min="0" value={f.salary} onChange={(e) => setF({ ...f, salary: e.target.value })} data-testid="staff-salary-input" /></Field>
            </div>
          </>}
          <DialogFooter><Button onClick={save} data-testid="staff-form-submit-button">Add staff</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
